import React, { useState, useEffect, useMemo } from 'react';
import { Search, Users, ShieldCheck, Zap, User as UserIcon, AlertCircle, Trash2 } from 'lucide-react';
import * as userService from '../services/userService';
import { ROLES, ROLE_LABELS, useAuth } from '../hooks/useAuth';

const ROLE_STYLES = {
  [ROLES.ADMIN]: { icon: ShieldCheck, badge: 'bg-purple-100 text-purple-800 border-purple-300' },
  [ROLES.USER]: { icon: UserIcon, badge: 'bg-gray-100 text-gray-700 border-gray-300' },
  [ROLES.INFERENCE_PLUS]: { icon: Zap, badge: 'bg-teal-100 text-teal-800 border-teal-300' },
  [ROLES.INFERENCE]: { icon: Zap, badge: 'bg-blue-100 text-blue-800 border-blue-300' },
};

const ROLE_HINTS = {
  [ROLES.ADMIN]: 'Full access, including provider credentials, GPU config and real cloud costs.',
  [ROLES.USER]: 'Launch and manage clusters, upload weights, curate images.',
  [ROLES.INFERENCE_PLUS]: 'Everything an inference account can do, plus launching, observing and tearing down its OWN clusters. Cannot curate images, manage registries or see cloud costs. Can spend credit.',
  [ROLES.INFERENCE]: 'Call and test endpoints only — cannot provision compute or see cloud costs.',
};

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setError(null);
      const res = await userService.listUsers();
      if (res.success) setUsers(res.users || []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (targetUser, role) => {
    if (role === targetUser.role) return;

    const label = ROLE_LABELS[role] || role;
    const name = targetUser.username || targetUser.email;
    if (!window.confirm(
      `Change ${name} to "${label}"?\n\n${ROLE_HINTS[role]}\n\nThis signs them out of all active sessions.`
    )) return;

    setSavingId(targetUser.id);
    setError(null);
    setNotice(null);
    try {
      const res = await userService.updateUserRole(targetUser.id, role);
      if (res.success) {
        setUsers(prev => prev.map(u => (u.id === targetUser.id ? { ...u, ...res.user } : u)));
        setNotice(`${name} is now ${label}. Their existing sessions were revoked.`);
      }
    } catch (err) {
      setError(err.message || 'Failed to update role');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (targetUser) => {
    const name = targetUser.username || targetUser.email;
    if (!window.confirm(
      `Permanently delete ${name}?\n\n` +
      'This removes the account and everything tied to it: sessions, API keys, ' +
      'credit balance and transaction history, saved presets, registries and ' +
      'uploaded weights.\n\nThis cannot be undone. To cut off access reversibly, ' +
      'set the role to Inference instead.'
    )) return;

    setDeletingId(targetUser.id);
    setError(null);
    setNotice(null);
    try {
      await userService.deleteUser(targetUser.id);
      setUsers(prev => prev.filter(u => u.id !== targetUser.id));
      setNotice(`${name} was deleted.`);
    } catch (err) {
      // A 409 names what the account still owns; it is the useful part.
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const counts = useMemo(() => users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {}), [users]);

  const filtered = useMemo(() => {
    let rows = users;
    if (roleFilter !== 'all') rows = rows.filter(u => u.role === roleFilter);
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(u =>
        u.email?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q) ||
        u.first_name?.toLowerCase().includes(q) ||
        u.last_name?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [users, search, roleFilter]);

  const displayName = (u) => {
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return full || u.username || u.email;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Users</h2>
          <p className="text-sm text-gray-600 mt-1">
            Assign account roles. Inference-only accounts can call endpoints but never provision compute.
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Users size={16} />
          <span>{users.length} total</span>
        </div>
      </div>

      {error && (
        <div className="flex items-start space-x-2 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        {Object.values(ROLES).map(role => {
          const { icon: Icon, badge } = ROLE_STYLES[role];
          return (
            <button
              key={role}
              onClick={() => setRoleFilter(roleFilter === role ? 'all' : role)}
              className={`text-left bg-white rounded-lg border p-4 transition-colors ${
                roleFilter === role ? 'border-purple-400 ring-1 ring-purple-200' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge}`}>
                  <Icon size={12} />
                  <span>{ROLE_LABELS[role]}</span>
                </span>
                <span className="text-xl font-semibold text-gray-900">{counts[role] || 0}</span>
              </div>
              <p className="text-xs text-gray-500 leading-snug">{ROLE_HINTS[role]}</p>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, username or email"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Joined</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading users...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No users match this filter.</td></tr>
              )}
              {!loading && filtered.map(u => {
                const isSelf = u.id === currentUser?.id;
                // Mirrors the backend guard: deleting the only admin would
                // leave nobody able to administer, and signup now only mints
                // inference accounts, so there would be no way back in.
                const isLastAdmin = u.role === ROLES.ADMIN && (counts[ROLES.ADMIN] || 0) <= 1;
                const { icon: Icon, badge } = ROLE_STYLES[u.role] || ROLE_STYLES[ROLES.USER];
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{displayName(u)}</div>
                      {u.username && <div className="text-xs text-gray-500">@{u.username}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '--'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium border ${badge}`}>
                          <Icon size={12} />
                          <span>{ROLE_LABELS[u.role] || u.role}</span>
                        </span>
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          /* Self-demotion is rejected by the backend too: it would
                             strip the caller's own admin access, and with a single
                             admin nobody could grant it back through the UI. */
                          disabled={isSelf || savingId === u.id}
                          title={isSelf ? 'You cannot change your own role' : undefined}
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs disabled:bg-gray-100 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {Object.values(ROLES).map(role => (
                            <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                          ))}
                        </select>
                        {savingId === u.id && <span className="text-xs text-gray-400">Saving...</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={isSelf || isLastAdmin || deletingId === u.id || savingId === u.id}
                        title={
                          isSelf ? 'You cannot delete your own account'
                            : isLastAdmin ? 'This is the last admin account'
                            : `Delete ${u.username || u.email}`
                        }
                        className="inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:text-gray-300 disabled:border-gray-200 disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <Trash2 size={12} />
                        <span>{deletingId === u.id ? 'Deleting...' : 'Delete'}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
